import React from "react";
import { cn } from "@/lib/utils";

function PulseBar({ className, delayMs = 0 }) {
  return (
    <div
      className={cn("bg-muted/90 animate-pulse rounded-md", className)}
      style={{ animationDelay: `${delayMs}ms` }}
    />
  );
}

/**
 * List grid skeleton — matches `grid-cols-1 sm:2 lg:3` + card padding (orders-style).
 */
export function LinkInBioListRowsSkeleton() {
  const placeholders = [0, 1, 2, 3, 4, 5];

  return (
    <div
      className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
      aria-hidden
    >
      {placeholders.map((i) => (
        <div
          key={i}
          className="border-border bg-card flex h-full min-h-[248px] flex-col rounded-xl border p-5 shadow-sm"
        >
          <div className="flex min-h-0 flex-1 flex-col space-y-5">
            <div className="flex min-w-0 items-start justify-between gap-2">
              <div className="min-w-0 flex-1 space-y-2">
                <PulseBar
                  className="h-5 w-full max-w-[min(100%,11rem)]"
                  delayMs={i * 45}
                />
                <PulseBar
                  className="h-5 w-full max-w-[min(100%,9rem)] opacity-90"
                  delayMs={i * 45 + 25}
                />
              </div>
              <PulseBar
                className="mt-0.5 h-5 w-11 shrink-0 rounded-full"
                delayMs={i * 45 + 40}
              />
            </div>
            <PulseBar
              className="h-4 w-full max-w-[14rem] font-mono opacity-70"
              delayMs={i * 45 + 55}
            />
          </div>

          <div className="border-border mt-auto grid grid-cols-2 gap-2 border-t pt-5">
            <PulseBar className="h-9 w-full rounded-md" delayMs={i * 45 + 75} />
            <PulseBar
              className="h-9 w-full rounded-md opacity-95"
              delayMs={i * 45 + 90}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Settings editor body (parent supplies PageHeader; optional back link is omitted here).
 */
export function LinkInBioSettingsPageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[1fr_320px] lg:items-start">
        <div className="border-border bg-card space-y-5 rounded-xl border p-6 shadow-sm">
          <PulseBar className="h-6 w-40" delayMs={0} />
          <div className="space-y-3 pt-1">
            <PulseBar className="h-24 w-full rounded-lg" delayMs={40} />
          </div>
          <div className="space-y-3 pt-2">
            <PulseBar className="h-10 w-full rounded-md" delayMs={70} />
            <PulseBar className="h-10 w-full rounded-md" delayMs={90} />
          </div>
        </div>
        <div className="border-border bg-card space-y-4 rounded-xl border p-6 shadow-sm">
          <PulseBar className="h-5 w-28" delayMs={50} />
          <PulseBar
            className="mx-auto aspect-[9/16] w-full max-w-[280px] rounded-2xl"
            delayMs={80}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Public page (customer-facing): warm paper tone, centered stack.
 */
export function LinkInBioPublicPageSkeleton() {
  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_120%_80%_at_50%_-10%,#ebe6dc_0%,#f7f4ee_42%,#f2efe8_100%)] px-5 py-14">
      <div className="mx-auto max-w-md space-y-8">
        <div className="flex flex-col items-center space-y-6">
          <div
            className="h-[4.5rem] w-[4.5rem] animate-pulse rounded-2xl bg-stone-300/75 shadow-inner ring-1 ring-stone-400/20"
            aria-hidden
          />
          <div className="w-full space-y-4 text-center">
            <div className="mx-auto h-6 w-40 animate-pulse rounded-md bg-stone-300/70" />
            <div className="mx-auto h-4 w-full max-w-[14rem] animate-pulse rounded bg-stone-300/55" />
          </div>
        </div>
        <div className="space-y-4 pt-1">
          <div className="h-[3.25rem] w-full animate-pulse rounded-2xl bg-stone-300/65 shadow-sm" />
          <div className="h-[3.25rem] w-full animate-pulse rounded-2xl bg-stone-300/55" />
          <div className="h-[3.25rem] w-full animate-pulse rounded-2xl bg-stone-300/45" />
        </div>
      </div>
    </div>
  );
}
